import { useState } from 'react'
import {
  Box, Typography, IconButton, TextField, Switch, FormControlLabel,
  Collapse, Chip, Button, Select, MenuItem, FormControl, InputLabel,
  Tooltip, Divider,
} from '@mui/material'
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Add as AddIcon,
  Close as RemoveIcon,
} from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'
import {
  FIELD_TYPES, FIELD_TYPE_LABELS, FieldType, FieldOption,
  LeadFormField, LEAD_MAPPING_OPTIONS,
} from '../../lib/api/leadFormsApi'

interface FieldEditorProps {
  field:    LeadFormField
  index:    number
  onUpdate: (id: string, updates: Partial<LeadFormField>) => void
  onDelete: (id: string) => void
  dragHandleProps?: any
}

const HAS_OPTIONS: FieldType[] = ['dropdown', 'radio', 'checkbox', 'multi_select']

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    '& fieldset': { borderColor: themeColors.border },
    '&:hover fieldset': { borderColor: themeColors.primary },
    '&.Mui-focused fieldset': { borderColor: themeColors.primary, borderWidth: '1px' },
  },
  '& .MuiInputBase-input': { fontSize: '12px' },
  '& .MuiInputLabel-root': { fontSize: '12px' },
}

export const FieldEditor = ({ field, index, onUpdate, onDelete, dragHandleProps }: FieldEditorProps) => {
  const [expanded, setExpanded] = useState(false)

  const mappingOptions = LEAD_MAPPING_OPTIONS

  const handleOptionAdd = () => {
    const existing = field.configuration?.options ?? []
    const newOpt: FieldOption = { label: `Option ${existing.length + 1}`, value: `option_${existing.length + 1}` }
    onUpdate(field.id, { configuration: { ...field.configuration, options: [...existing, newOpt] } })
  }

  const handleOptionChange = (idx: number, key: 'label' | 'value', val: string) => {
    const options = [...(field.configuration?.options ?? [])]
    options[idx] = { ...options[idx], [key]: val }
    onUpdate(field.id, { configuration: { ...field.configuration, options } })
  }

  const handleOptionRemove = (idx: number) => {
    const options = (field.configuration?.options ?? []).filter((_, i) => i !== idx)
    onUpdate(field.id, { configuration: { ...field.configuration, options } })
  }

  return (
    <Box
      sx={{
        border: `1px solid ${themeColors.border}`,
        borderRadius: '8px',
        bgcolor: '#fff',
        overflow: 'hidden',
        boxShadow: themeColors.shadow.sm,
        '&:hover': { borderColor: `${themeColors.primary}60` },
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: field.isDefault ? '#f8f7fc' : '#fff',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Drag handle */}
        <Box
          {...dragHandleProps}
          onClick={(e: any) => e.stopPropagation()}
          sx={{ cursor: 'grab', color: themeColors.text.muted, display: 'flex', alignItems: 'center' }}
        >
          <DragIcon sx={{ fontSize: 18 }} />
        </Box>

        {/* Index badge */}
        <Box
          sx={{
            width: 22, height: 22, borderRadius: '50%', bgcolor: themeColors.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#fff' }}>{index + 1}</Typography>
        </Box>

        {/* Label + type */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary, lineHeight: 1.2 }}>
            {field.label || 'Untitled Field'}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary }}>
            {FIELD_TYPE_LABELS[field.fieldType]} {field.required && '· Required'}
          </Typography>
        </Box>

        {/* Chips */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {field.isDefault && (
            <Chip label="Default" size="small" sx={{ fontSize: '10px', height: 18, bgcolor: themeColors.infoLight, color: themeColors.primary }} />
          )}
          {!field.isVisible && (
            <Chip label="Hidden" size="small" sx={{ fontSize: '10px', height: 18, bgcolor: '#f3f2f2', color: themeColors.text.secondary }} />
          )}
        </Box>

        {/* Delete */}
        {!field.isDefault && (
          <Tooltip title="Remove field">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(field.id) }}
              sx={{ color: themeColors.error, '&:hover': { bgcolor: themeColors.errorLight } }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Expand */}
        <IconButton size="small" sx={{ color: themeColors.text.secondary }}>
          {expanded ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      {/* Expanded config */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {/* Label */}
          <TextField
            label="Label"
            value={field.label}
            onChange={(e) => onUpdate(field.id, { label: e.target.value })}
            size="small"
            fullWidth
            sx={fieldSx}
          />

          {/* Field Type (disabled for default fields) */}
          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel sx={{ fontSize: '12px' }}>Field Type</InputLabel>
            <Select
              label="Field Type"
              value={field.fieldType}
              onChange={(e) => onUpdate(field.id, { fieldType: e.target.value as FieldType })}
              disabled={field.isDefault}
              sx={{ fontSize: '12px' }}
            >
              {FIELD_TYPES.map(t => (
                <MenuItem key={t} value={t} sx={{ fontSize: '12px' }}>{FIELD_TYPE_LABELS[t]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Placeholder */}
          <TextField
            label="Placeholder"
            value={field.placeholder ?? ''}
            onChange={(e) => onUpdate(field.id, { placeholder: e.target.value })}
            size="small"
            fullWidth
            sx={fieldSx}
          />

          {/* Internal Field Name */}
          <TextField
            label="Internal Field Name"
            value={field.fieldName}
            onChange={(e) => onUpdate(field.id, { fieldName: e.target.value })}
            size="small"
            fullWidth
            disabled={field.isDefault}
            sx={fieldSx}
          />

          {/* Mapping */}
          <FormControl size="small" fullWidth sx={fieldSx}>
            <InputLabel sx={{ fontSize: '12px' }}>Map to CRM Field</InputLabel>
            <Select
              label="Map to CRM Field"
              value={field.mappingField ?? ''}
              onChange={(e) => onUpdate(field.id, { mappingField: e.target.value })}
              sx={{ fontSize: '12px' }}
            >
              <MenuItem value="" sx={{ fontSize: '12px' }}><em>None (custom field)</em></MenuItem>
              {mappingOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '12px' }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Default value */}
          <TextField
            label="Default Value"
            value={field.configuration?.defaultValue ?? ''}
            onChange={(e) => onUpdate(field.id, { configuration: { ...field.configuration, defaultValue: e.target.value } })}
            size="small"
            fullWidth
            sx={fieldSx}
          />

          {/* Toggles */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', gridColumn: '1 / -1' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={field.required}
                  onChange={(e) => onUpdate(field.id, { required: e.target.checked })}
                  size="small"
                  sx={{ '& .MuiSwitch-thumb': { bgcolor: field.required ? themeColors.primary : '#aaa' } }}
                />
              }
              label={<Typography sx={{ fontSize: '12px' }}>Required</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={field.isVisible}
                  onChange={(e) => onUpdate(field.id, { isVisible: e.target.checked })}
                  size="small"
                />
              }
              label={<Typography sx={{ fontSize: '12px' }}>Visible on form</Typography>}
            />
          </Box>

          {/* Options (dropdown/radio/checkbox/multi_select) */}
          {HAS_OPTIONS.includes(field.fieldType) && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 1, color: themeColors.text.secondary }}>
                Options
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {(field.configuration?.options ?? []).map((opt, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      placeholder="Label"
                      value={opt.label}
                      onChange={(e) => handleOptionChange(i, 'label', e.target.value)}
                      size="small"
                      sx={{ flex: 1, ...fieldSx }}
                    />
                    <TextField
                      placeholder="Value"
                      value={opt.value}
                      onChange={(e) => handleOptionChange(i, 'value', e.target.value)}
                      size="small"
                      sx={{ flex: 1, ...fieldSx }}
                    />
                    <IconButton size="small" onClick={() => handleOptionRemove(i)} sx={{ color: themeColors.error }}>
                      <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOptionAdd}
                  sx={{ alignSelf: 'flex-start', fontSize: '12px', color: themeColors.primary, mt: 0.5 }}
                >
                  Add Option
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
